<script setup lang="ts">
import { computed, inject } from 'vue'
import BbelField from '@/views/builder/components/bbel/BbelField.vue'
import AdvancedSettings from './AdvancedSettings.vue'
import ConnectionPicker from './ConnectionPicker.vue'
import FieldValueEditor from './FieldValueEditor.vue'
import type { SftpServiceModel, FieldValue } from '@/types'
import { SftpOperation } from '@/types'
import { BBEL_SCOPE_KEY, EMPTY_BBEL_SCOPE, type BbelScopeContext } from '@/views/builder/composables/bbel-context'
import type { ScopeDescriptor } from '@/types/bbel-types'

const props = defineProps<{
  modelValue: SftpServiceModel
}>()

const emit = defineEmits<{
  'update:modelValue': [value: SftpServiceModel]
}>()

const bbelScopeCtx = inject<BbelScopeContext | undefined>(BBEL_SCOPE_KEY, undefined)
const scope = computed<ScopeDescriptor>(() => bbelScopeCtx?.value ?? EMPTY_BBEL_SCOPE)

const operations = [
  SftpOperation.Upload,
  SftpOperation.Download,
  SftpOperation.List,
  SftpOperation.Delete,
  SftpOperation.Rename,
]

function update(updates: Partial<SftpServiceModel>) {
  emit('update:modelValue', { ...props.modelValue, ...updates })
}

const isUpload = computed(() => props.modelValue.operation === SftpOperation.Upload)
const isRename = computed(() => props.modelValue.operation === SftpOperation.Rename)
const isList = computed(() => props.modelValue.operation === SftpOperation.List)

// Upload's payload defaults to an expression until the author toggles it to a binary reference.
const source = computed<FieldValue>(
  () => props.modelValue.source ?? { $type: 'expression', expression: '' },
)
</script>

<template>
  <div>
    <ConnectionPicker
      :model-value="modelValue.connectionName"
      @update:model-value="update({ connectionName: $event })"
    />

    <div class="mb-3">
      <label class="form-label">Operation</label>
      <select
        :value="modelValue.operation"
        @change="update({ operation: ($event.target as HTMLSelectElement).value as SftpOperation })"
        class="form-select"
      >
        <option v-for="op in operations" :key="op" :value="op">{{ op }}</option>
      </select>
    </div>

    <div class="mb-3">
      <label class="form-label">{{ isList ? 'Remote directory' : 'Remote path' }}</label>
      <BbelField
        :model-value="modelValue.remotePath"
        :scope="scope"
        single-line
        :placeholder="isList ? '/outbound' : '/outbound/report.csv'"
        @update:model-value="update({ remotePath: $event })"
      />
    </div>

    <template v-if="isUpload">
      <div class="mb-3">
        <label class="form-label">Remote file name</label>
        <BbelField
          :model-value="modelValue.remoteFileName ?? ''"
          :scope="scope"
          single-line
          placeholder="report-{{ system.now }}.csv"
          @update:model-value="update({ remoteFileName: $event })"
        />
      </div>

      <div class="form-check form-switch mb-3">
        <input
          id="sftp-overwrite"
          class="form-check-input"
          type="checkbox"
          :checked="modelValue.overwrite"
          @change="update({ overwrite: ($event.target as HTMLInputElement).checked })"
        />
        <label class="form-check-label" for="sftp-overwrite">
          Overwrite if the remote file already exists
        </label>
      </div>

      <div class="mb-3">
        <label class="form-label">Source</label>
        <FieldValueEditor
          :model-value="source"
          placeholder="File contents to upload"
          @update:model-value="update({ source: $event })"
        />
        <div class="form-text">
          What to upload — a binary reference streams an upstream file straight to the server; an
          expression uploads rendered text.
        </div>
      </div>
    </template>

    <div v-if="isRename" class="mb-3">
      <label class="form-label">New path</label>
      <BbelField
        :model-value="modelValue.newPath ?? ''"
        :scope="scope"
        single-line
        placeholder="/archive/report.csv"
        @update:model-value="update({ newPath: $event })"
      />
    </div>

    <AdvancedSettings />
  </div>
</template>
